import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Page publique (sans authentification) servant un lien magique unique.
 * Permet à un invité de :
 *   - répondre / compléter le questionnaire de l'événement
 *   - finaliser le paiement Stripe de son inscription
 * sans avoir à se connecter à la plateforme.
 *
 * Props :
 *   - token    : string (signed_id de l'inscription)
 *   - focus    : 'questionnaire' | 'payment' | null
 */
const MagicRegistration = ({ token, focus }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState(focus || 'overview');
  const [toast, setToast]     = useState(null);

  // Form state
  const [answers, setAnswers] = useState({});
  const [imageRights, setImageRights] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Payment state
  const [stripe, setStripe] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [paying, setPaying] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ----- Fetch initial -----

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/v1/r/${token}`, {
        headers: { 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Lien invalide ou expiré');
      }
      setData(json);
      setAnswers(json.questionnaire.answers || {});
      setImageRights(json.registration.image_rights_accepted || false);
      setRulesAccepted(json.registration.rules_accepted || false);

      // Auto-focus le bon onglet
      if (!focus) {
        if (json.needs.questionnaire) setTab('questionnaire');
        else if (json.needs.payment)  setTab('payment');
        else setTab('overview');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, focus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ----- Questionnaire -----

  const setAnswer = (name, value) => setAnswers(prev => ({ ...prev, [name]: value }));

  const submitQuestionnaire = async () => {
    try {
      setSubmitting(true);
      const res = await fetch(`/api/v1/r/${token}/questionnaire`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          custom_data: answers,
          image_rights_accepted: imageRights,
          rules_accepted: rulesAccepted
        })
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Enregistrement impossible');
      }
      showToast('Questionnaire enregistré ✅');
      setData(json);
      // Aller naturellement au paiement si nécessaire
      if (json.needs.payment) setTab('payment');
      else setTab('overview');
    } catch (e) {
      showToast(`❌ ${e.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Paiement Stripe -----

  const loadStripe = useCallback(async (publishableKey, clientSecret) => {
    if (!window.Stripe) {
      // Charger Stripe.js dynamiquement
      await new Promise((resolve, reject) => {
        const existing = document.getElementById('stripe-js');
        if (existing) { existing.addEventListener('load', resolve); return; }
        const script = document.createElement('script');
        script.id    = 'stripe-js';
        script.src   = 'https://js.stripe.com/v3/';
        script.onload  = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
    const s = window.Stripe(publishableKey);
    setStripe(s);

    // Monter Card Element
    setTimeout(() => {
      const elements = s.elements({ clientSecret });
      const card = elements.create('payment');
      const mount = document.getElementById('stripe-card-element');
      if (mount) {
        card.mount('#stripe-card-element');
        setCardElement({ elements, card });
      }
    }, 100);
  }, []);

  const initPayment = async () => {
    try {
      setStripeLoading(true);
      setStripeError(null);
      const res = await fetch(`/api/v1/r/${token}/payment_intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Impossible de préparer le paiement');
      }
      setPaymentIntent(json);
      await loadStripe(json.publishable_key, json.client_secret);
    } catch (e) {
      setStripeError(e.message);
    } finally {
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'payment' && data?.needs?.payment && !paymentIntent && !stripeLoading) {
      initPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, data]);

  const submitPayment = async () => {
    if (!stripe || !cardElement) return;
    try {
      setPaying(true);
      setStripeError(null);

      const { error: confirmError } = await stripe.confirmPayment({
        elements: cardElement.elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        throw new Error(confirmError.message || 'Le paiement a échoué');
      }

      // Confirmer côté serveur
      const res = await fetch(`/api/v1/r/${token}/confirm_payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Confirmation impossible');
      }
      showToast('Paiement confirmé 🎉');
      setData(json);
      setTab('overview');
    } catch (e) {
      setStripeError(e.message);
      showToast(`❌ ${e.message}`, 'error');
    } finally {
      setPaying(false);
    }
  };

  // ----- Render -----

  if (loading) return <div style={S.page}><div style={S.center}>Chargement…</div></div>;

  if (error) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.h1}>Lien indisponible</div>
          <div style={S.error}>❌ {error}</div>
          <div style={S.help}>Le lien a peut-être expiré ou a déjà été utilisé. Contactez l'organisateur pour en demander un nouveau, ou connectez-vous à la plateforme pour gérer votre inscription.</div>
          <a href="/" style={S.btnSecondary}>← Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { event, registration, questionnaire, payment, needs } = data;
  const allDone = !needs.questionnaire && !needs.payment;

  return (
    <div style={S.page}>
      {toast && (
        <div style={{ ...S.toast, background: toast.type === 'error' ? '#7c1c2a' : '#1a3a2a' }}>
          {toast.message}
        </div>
      )}

      <div style={S.container}>
        <div style={S.heroCard}>
          <div style={{ fontSize: '.82rem', color: '#7a4050', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Finalisez votre inscription
          </div>
          <div style={S.h1}>{event.title}</div>
          <div style={{ color: '#7a4050' }}>
            📅 {new Date(event.start_date).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })} <br />
            📍 {event.location}
          </div>
          <div style={{ marginTop: 14, color: '#2c0e16' }}>
            Bonjour <strong>{registration.user.first_name} {registration.user.last_name}</strong>{registration.is_plus_one ? ' (+1)' : ''} ✨
          </div>
          <div style={{ marginTop: 4, color: '#7a4050', fontSize: '.88rem' }}>{registration.user.email}{registration.user.phone ? ` · ${registration.user.phone}` : ''}</div>

          <div style={S.statusGrid}>
            <StatusCard
              icon="📝"
              title="Questionnaire"
              status={!questionnaire.required ? 'na' : (questionnaire.completed ? 'done' : 'pending')}
              onAction={() => setTab('questionnaire')}
            />
            <StatusCard
              icon="💶"
              title={`Paiement${payment.required ? ` (${payment.amount}€)` : ''}`}
              status={!payment.required ? 'na' : (payment.paid ? 'done' : 'pending')}
              onAction={() => setTab('payment')}
            />
          </div>

          {allDone && (
            <div style={S.successBanner}>
              🎉 Votre inscription est complète ! Rendez-vous le {new Date(event.start_date).toLocaleDateString('fr-FR')}.
            </div>
          )}
        </div>

        <div style={S.tabs}>
          <button style={S.tab(tab === 'overview')} onClick={() => setTab('overview')}>👀 Vue d'ensemble</button>
          {questionnaire.required && <button style={S.tab(tab === 'questionnaire')} onClick={() => setTab('questionnaire')}>📝 Questionnaire</button>}
          {payment.required && !payment.paid && <button style={S.tab(tab === 'payment')} onClick={() => setTab('payment')}>💶 Paiement</button>}
        </div>

        {tab === 'overview' && (
          <div style={S.card}>
            <div style={S.h2}>Détails de votre inscription</div>
            <Row label="Événement"  value={event.title} />
            <Row label="Date"       value={new Date(event.start_date).toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' })} />
            <Row label="Lieu"       value={event.location} />
            <Row label="Organisateur" value={event.organizer_name} />
            <Row label="Statut"     value={registration.status === 'waitlist' ? 'Liste d\'attente' : registration.status} />
            {payment.required && (
              <Row
                label="Paiement"
                value={payment.paid
                  ? `✅ ${payment.amount}€ réglés${payment.status ? ` (${payment.status})` : ''}`
                  : `⏳ ${payment.amount}€ à régler`}
              />
            )}
            {questionnaire.required && (
              <Row
                label="Questionnaire"
                value={questionnaire.completed
                  ? `✅ Complété${questionnaire.completed_at ? ` le ${new Date(questionnaire.completed_at).toLocaleDateString('fr-FR')}` : ''}`
                  : '❌ En attente'}
              />
            )}

            {/* Réponses au questionnaire en lecture seule */}
            {questionnaire.required && questionnaire.completed && questionnaire.fields?.length > 0 && (
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(124,40,55,0.15)' }}>
                <div style={{ ...S.h2, fontSize: '1rem', marginBottom: 10 }}>📝 Vos réponses</div>
                {questionnaire.fields.map(field => {
                  const rawAnswer = answers[field.name];
                  const displayValue = formatAnswer(rawAnswer, field);
                  return (
                    <Row
                      key={field.name}
                      label={field.label}
                      value={displayValue || <em style={{ color: '#9e6575' }}>(non renseigné)</em>}
                    />
                  );
                })}
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button style={S.btnSecondary} onClick={() => setTab('questionnaire')}>Modifier mes réponses</button>
                </div>
              </div>
            )}

            {/* Récap engagements */}
            {(registration.image_rights_accepted || registration.rules_accepted) && (
              <div style={{ marginTop: 14, fontSize: '.85rem', color: '#7a4050' }}>
                {registration.image_rights_accepted && <div>✅ Droit à l'image accepté</div>}
                {registration.rules_accepted && <div>✅ Règlement intérieur accepté</div>}
              </div>
            )}
          </div>
        )}

        {tab === 'questionnaire' && questionnaire.required && (
          <div style={S.card}>
            <div style={S.h2}>📝 Questionnaire</div>
            <p style={{ color: '#7a4050', marginTop: 0 }}>Quelques questions pour finaliser votre inscription.</p>
            {questionnaire.fields.map(field => (
              <QuestionField key={field.name} field={field} value={answers[field.name]} onChange={(v) => setAnswer(field.name, v)} />
            ))}

            <label style={S.checkbox}>
              <input type="checkbox" checked={imageRights} onChange={e => setImageRights(e.target.checked)} />
              <span>J'accepte le droit à l'image</span>
            </label>
            <label style={S.checkbox}>
              <input type="checkbox" checked={rulesAccepted} onChange={e => setRulesAccepted(e.target.checked)} />
              <span>J'accepte le règlement intérieur</span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 10 }}>
              <button style={S.btnSecondary} onClick={() => setTab('overview')}>Annuler</button>
              <button style={S.btnPrimary} onClick={submitQuestionnaire} disabled={submitting}>
                {submitting ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {tab === 'payment' && payment.required && (
          <div style={S.card}>
            <div style={S.h2}>💶 Paiement sécurisé</div>
            {payment.paid ? (
              <div style={S.successBanner}>✅ Votre paiement de {payment.amount}€ est confirmé.</div>
            ) : (
              <>
                {paymentIntent && (
                  <div style={S.amountBox}>
                    <Row label="Montant inscription" value={`${paymentIntent.base_amount}€`} />
                    <Row label="Frais bancaires" value={`${paymentIntent.stripe_fee}€`} />
                    <Row label="Total" value={<strong>{paymentIntent.amount}€</strong>} />
                  </div>
                )}
                {stripeLoading && <div style={S.center}>Préparation du paiement…</div>}
                {stripeError && <div style={S.error}>❌ {stripeError}</div>}
                <div id="stripe-card-element" style={{ marginTop: 14, padding: 14, border: '1px solid rgba(124,40,55,0.20)', borderRadius: 10, background: '#fff', minHeight: 48 }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 10 }}>
                  <button style={S.btnSecondary} onClick={() => setTab('overview')}>Annuler</button>
                  <button style={S.btnPrimary} onClick={submitPayment} disabled={paying || !stripe || !cardElement}>
                    {paying ? 'Paiement en cours…' : `Payer ${paymentIntent?.amount ?? payment.amount}€`}
                  </button>
                </div>
                <div style={S.help}>🔒 Paiement sécurisé par Stripe. Vos informations bancaires ne transitent pas par nos serveurs.</div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// === Sub-components =====================================================

// Formate une réponse de questionnaire pour l'affichage en lecture seule.
// Gère : booleens, listes (cases à cocher multiples), valeurs avec libellé d'option.
const formatAnswer = (value, field) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (Array.isArray(value)) {
    const labels = value.map(v => {
      const opt = field?.options?.find(o => o.value === v);
      return opt ? opt.label : String(v);
    });
    return labels.join(', ');
  }
  // Pour les selects / radios : essayer de trouver le label de l'option
  if (field?.options?.length > 0) {
    const opt = field.options.find(o => o.value === value);
    if (opt) return opt.label;
  }
  return String(value);
};

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(124,40,55,0.10)' }}>
    <span style={{ color: '#7a4050' }}>{label}</span>
    <span style={{ color: '#2c0e16', fontWeight: 600, textAlign: 'right' }}>{value || '—'}</span>
  </div>
);

const StatusCard = ({ icon, title, status, onAction }) => {
  const cfg = status === 'done'
    ? { color: '#15803d', bg: 'rgba(34,197,94,0.15)', label: '✅ Complété', clickable: false }
    : status === 'pending'
      ? { color: '#dc3545', bg: 'rgba(220,53,69,0.15)', label: '⏳ À faire', clickable: true }
      : { color: '#7a4050', bg: '#f0e4e8', label: 'Non requis', clickable: false };
  return (
    <button onClick={cfg.clickable ? onAction : undefined}
      disabled={!cfg.clickable}
      style={{ ...S.statusCard, cursor: cfg.clickable ? 'pointer' : 'default', opacity: cfg.clickable || status === 'done' ? 1 : 0.7 }}>
      <div style={{ fontSize: '1.6rem' }}>{icon}</div>
      <div style={{ fontWeight: 700, color: '#2c0e16' }}>{title}</div>
      <div style={{ marginTop: 4, padding: '4px 10px', borderRadius: 6, background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '.82rem' }}>
        {cfg.label}
      </div>
    </button>
  );
};

const QuestionField = ({ field, value, onChange }) => {
  const label = (
    <label style={S.qLabel}>
      {field.label} {field.required && <span style={{ color: '#dc3545' }}>*</span>}
    </label>
  );

  if (field.type === 'select' || field.type === 'radio') {
    return (
      <div style={S.qBlock}>
        {label}
        <select style={S.qInput} value={value ?? ''} onChange={e => onChange(e.target.value)}>
          <option value="">— Choisir —</option>
          {field.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'checkbox' || field.type === 'multi_select') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
    return (
      <div style={S.qBlock}>
        {label}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {field.options.map(opt => (
            <label key={opt.value} style={S.checkbox}>
              <input type="checkbox" checked={arr.includes(opt.value)} onChange={() => toggle(opt.value)} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div style={S.qBlock}>
        <label style={S.checkbox}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
          <span>{field.label} {field.required && <span style={{ color: '#dc3545' }}>*</span>}</span>
        </label>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div style={S.qBlock}>
        {label}
        <textarea style={{ ...S.qInput, minHeight: 100, fontFamily: 'inherit' }} value={value ?? ''} onChange={e => onChange(e.target.value)} />
      </div>
    );
  }

  return (
    <div style={S.qBlock}>
      {label}
      <input style={S.qInput} type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
        value={value ?? ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
};

// === Styles =============================================================

const S = {
  page: { minHeight: '100vh', background: '#f7eef2', padding: '120px 16px 40px', fontFamily: 'inherit' },
  container: { maxWidth: 760, margin: '0 auto' },
  toast: { position: 'fixed', top: 24, right: 24, color: '#fff', padding: '14px 24px', borderRadius: 12, zIndex: 9999, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' },
  center: { textAlign: 'center', color: '#2c0e16', padding: 24 },
  card: { background: '#fff', border: '1px solid rgba(124,40,55,0.12)', borderRadius: 16, padding: 22, marginTop: 16 },
  heroCard: { background: '#fff', border: '1px solid rgba(124,40,55,0.12)', borderRadius: 16, padding: 22 },
  h1: { color: '#2c0e16', fontSize: 'clamp(1.4rem, 3vw, 1.9rem)', fontWeight: 800, margin: '6px 0 10px' },
  h2: { color: '#2c0e16', fontSize: '1.15rem', fontWeight: 800, marginTop: 0 },
  statusGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 18 },
  statusCard: { background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.18)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' },
  successBanner: { marginTop: 14, background: 'rgba(34,197,94,0.12)', color: '#15803d', border: '1px solid rgba(34,197,94,0.30)', padding: 12, borderRadius: 10, fontWeight: 600 },
  tabs: { display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' },
  tab: (active) => ({
    background: active ? 'linear-gradient(135deg,#7c2837,#a8384e)' : 'transparent',
    color: active ? '#fff' : '#2c0e16',
    border: active ? 'none' : '1px solid rgba(124,40,55,0.25)',
    padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '.92rem'
  }),
  btnPrimary:   { background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', border: 'none', padding: '12px 22px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '.95rem' },
  btnSecondary: { background: '#fdf6f8', color: '#2c0e16', border: '1px solid rgba(124,40,55,0.25)', padding: '12px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: '.92rem', textDecoration: 'none', display: 'inline-block' },
  error:        { color: '#dc3545', background: 'rgba(220,53,69,0.10)', border: '1px solid rgba(220,53,69,0.30)', padding: 12, borderRadius: 10, marginTop: 10 },
  help:         { color: '#7a4050', fontSize: '.85rem', marginTop: 14 },
  amountBox:    { background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.15)', borderRadius: 10, padding: 12, marginTop: 8 },
  qBlock:       { marginBottom: 14 },
  qLabel:       { display: 'block', color: '#2c0e16', fontWeight: 600, marginBottom: 6, fontSize: '.92rem' },
  qInput:       { width: '100%', background: '#fff', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 12px', borderRadius: 10, outline: 'none', fontSize: '.92rem', boxSizing: 'border-box' },
  checkbox:     { display: 'flex', gap: 8, alignItems: 'center', color: '#2c0e16', cursor: 'pointer', marginTop: 8 }
};

export default MagicRegistration;
